import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authPathWithPlan, mapPricingPlans } from './sharedPricing';
import {
  finalCta,
  flowSteps,
  heroDescription,
  heroEyebrow,
  heroTitleLines,
  productHighlights,
  statsBar,
  testimonials,
} from './landingCopy';

export default function LandingMinimal({
  brand = 'Workspace',
  logoUrl = null,
  pricingPlans = [],
  authPath = '/auth',
  primaryColor = '#18181b',
} = {}) {
  const navigate = useNavigate();
  const plans = mapPricingPlans(pricingPlans);
  const title = heroTitleLines();
  const products = productHighlights(brand);
  const steps = flowSteps(brand);
  const quotes = testimonials(brand);
  const cta = finalCta(brand);
  const accent = primaryColor || '#18181b';
  const sans = { fontFamily: 'system-ui, sans-serif' };

  return (
    <div className="min-h-screen bg-[#f7f5f2] text-zinc-900" data-testid="wl-landing-minimal" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
      <nav className="border-b border-zinc-200/80 bg-[#f7f5f2]/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? <img src={logoUrl} alt={brand} className="w-7 h-7 rounded object-cover" /> : null}
            <span className="text-lg tracking-tight">{brand}</span>
          </div>
          <div className="flex items-center gap-6 text-sm" style={sans}>
            <a href="#products" className="text-zinc-500 hover:text-zinc-900">Products</a>
            <a href="#flow" className="text-zinc-500 hover:text-zinc-900">How it works</a>
            <a href="#pricing" className="text-zinc-500 hover:text-zinc-900">Pricing</a>
            <button type="button" onClick={() => navigate(authPath)} className="underline underline-offset-4">
              Sign in
            </button>
          </div>
        </div>
      </nav>

      <section className="max-w-3xl mx-auto px-6 pt-24 pb-16">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6" style={sans}>{heroEyebrow()}</p>
        <h1 className="text-4xl md:text-5xl leading-tight mb-4 font-normal">
          {title.line1}<br />{title.line2}.
        </h1>
        <p className="text-lg text-zinc-600 leading-relaxed mb-10 max-w-xl" style={sans}>
          {heroDescription(brand)}
        </p>
        <Button
          onClick={() => navigate(authPathWithPlan(authPath, plans[0]?.key))}
          className="rounded-none px-8 py-6 text-sm tracking-wide uppercase text-white"
          style={{ background: accent, ...sans }}
        >
          Start for free
        </Button>
      </section>

      <section className="border-y border-zinc-200 py-12 px-6" style={sans}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {statsBar().map((s) => (
            <div key={s.label}>
              <p className="text-2xl font-semibold text-zinc-900">{s.value}</p>
              <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="products" className="max-w-5xl mx-auto px-6 py-20">
        <h2 className="text-3xl mb-3">Everything you need to launch and grow</h2>
        <p className="text-zinc-600 mb-10 max-w-xl" style={sans}>
          From websites and AI tools to marketing and lead generation — {brand} puts your whole stack in one place.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((p) => (
            <div key={p.title} className="border-t border-zinc-300 pt-5">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2" style={sans}>{p.tag}</p>
              <h3 className="text-xl mb-1">{p.title}</h3>
              <p className="text-sm text-zinc-500 mb-2" style={sans}>{p.subtitle}</p>
              <p className="text-sm text-zinc-600 leading-relaxed" style={sans}>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="flow" className="border-t border-zinc-200 py-20 px-6 bg-white/40">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl mb-10">How it works</h2>
          <div className="space-y-8">
            {steps.map((s) => (
              <div key={s.step} className="grid md:grid-cols-[80px_1fr] gap-4 border-b border-zinc-200 pb-8">
                <p className="text-3xl text-zinc-300">{s.step}</p>
                <div>
                  <h3 className="text-xl mb-1">{s.title}</h3>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2" style={sans}>{s.module}</p>
                  <p className="text-sm text-zinc-600 leading-relaxed" style={sans}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-zinc-200 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl mb-3 text-center">Start free. Scale as you grow.</h2>
          <p className="text-center text-zinc-600 mb-12" style={sans}>Pick a plan — modules unlock when you create your account.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200">
            {plans.map((plan) => (
              <div key={plan.key || plan.name} className={`bg-[#f7f5f2] p-8 flex flex-col ${plan.highlight ? 'ring-1 ring-inset ring-zinc-900' : ''}`}>
                <h3 className="text-xl mb-1">{plan.name}</h3>
                <p className="text-sm text-zinc-500 mb-4" style={sans}>{plan.desc}</p>
                <div className="text-3xl mb-6">{plan.price}</div>
                <ul className="space-y-2 flex-1 mb-8" style={sans}>
                  {(plan.features || []).slice(0, 8).map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-zinc-700">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(authPathWithPlan(authPath, plan.key))}
                  variant="outline"
                  className="rounded-none border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white"
                  style={sans}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-200 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl text-center mb-2">Loved by builders worldwide</h2>
          <p className="text-center text-sm text-zinc-500 mb-10 flex items-center justify-center gap-1" style={sans}>
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
            <span className="ml-2">4.9 / 5 from 2,400+ reviews</span>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quotes.map((t) => (
              <div key={t.name} className="border-t border-zinc-300 pt-5">
                <p className="text-sm text-zinc-700 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <p className="text-sm font-medium" style={sans}>{t.name}</p>
                <p className="text-xs text-zinc-500" style={sans}>{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-200 py-24 px-6 text-center">
        <h2 className="text-3xl md:text-4xl mb-4 max-w-2xl mx-auto">{cta.title}</h2>
        <p className="text-zinc-600 mb-8" style={sans}>{cta.subtitle}</p>
        <Button
          onClick={() => navigate(authPathWithPlan(authPath, plans[0]?.key))}
          className="rounded-none px-10 py-6 text-sm tracking-wide uppercase text-white"
          style={{ background: accent, ...sans }}
        >
          Get started free
        </Button>
        <p className="text-xs text-zinc-500 mt-4" style={sans}>{cta.note}</p>
      </section>

      <footer className="py-10 text-center text-sm text-zinc-500" style={sans}>
        © {new Date().getFullYear()} {brand}
      </footer>
    </div>
  );
}
